interface MinimalRow {
  productID: string | number;
  stableRowID: string | number;
  amount: string | number;
  containerID?: string | number;
  containerAmount?: string | number;
  parentRowID?: string | number;
  jdoc?: {
    BrazilPOS?: {
      parentRowID?: number;
    };
  };
}

interface MinimalProduct {
  productID: string | number;
  containerID?: string | number;
  containerAmount?: string | number;
}

/**
 * From the given rows finds containers (deposit fee) that belong to the given parent row.
 * Uses jdoc.BrazilPOS.parentRowID or containerID with containerAmount fields to determine
 * if a container row belongs to the parent row. Containers are found recursively but all
 * are put in a flat array to match behavior of prepareShoppingCartForCalculate.
 *
 * So if container product are configured as such a->b, b->c, c->d
 * where a is the parent row and [b, c, d] is the list of rows
 * then the return value will be [b, c, d]
 *
 * @example
 * const parentRow: MinimalRow = {
 *   stableRowID: 123,
 *   productID: 321,
 *   amount: 1,
 *   containerID: 654,
 *   containerAmount: 2,
 * };
 * const rows: MinimalRow[] = [
 *   {
 *     stableRowID: 999,
 *     productID: 999,
 *     amount: 1,
 *   },
 *   {
 *     stableRowID: 456,
 *     productID: 654,
 *     amount: 2,
 *     jdoc: { BrazilPOS: { parentRowID: 123 } },
 *     containerID: 987,
 *     containerAmount: 1,
 *   },
 *   { stableRowID: 789, productID: 987, amount: 1 },
 * ];
 * findMatchingContainers(parentRow, rows);
 * // Returns: [
 * //   {
 * //     stableRowID: 456,
 * //     productID: 654,
 * //     amount: 2,
 * //     jdoc: { BrazilPOS: { parentRowID: 123 } },
 * //     containerID: 987,
 * //     containerAmount: 1,
 * //   },
 * //   { stableRowID: 789, productID: 987, amount: 1 },
 * // ]
 */
function findMatchingContainers<Row extends MinimalRow>(
  parent: Row,
  rows: Row[],
) {
  const exactMatches = rows.filter(container => {
    const isChildOfParent = [
      Number(container.jdoc?.BrazilPOS?.parentRowID),
      Number(container.parentRowID),
    ].includes(Number(parent.stableRowID));
    return isChildOfParent;
  });

  // In case of a badly saved sale row or if a sale was not created using Brazil POS
  // try to find containers by container id and container amount
  const looseMatch = rows.find(container => {
    const isMatchingContainer =
      Number(container.productID) === Number(parent.containerID);
    const matchesAmount =
      Number(container.amount) ===
      Number(parent.containerAmount ?? 1) * Number(parent.amount);
    return isMatchingContainer && matchesAmount;
  });
  const looseMatches = looseMatch ? [looseMatch] : [];

  const matches = exactMatches.length ? exactMatches : looseMatches;

  return matches.flatMap(match => {
    if (!Number(match.containerID)) return [match];
    const usedRows = [Number(parent.stableRowID), Number(match.stableRowID)];
    // Recursion because for some reason containers can have containers
    const nestedContainers = findMatchingContainers(
      match,
      rows.filter(
        row =>
          !usedRows.includes(Number(row.stableRowID)) &&
          !matches.some(m => Number(m.stableRowID) === Number(row.stableRowID)),
      ),
    );
    // Max container tree depth is 2, this is how prepareShoppingCartForCalculate does it
    return [match, ...nestedContainers];
  });
}

/**
 * Given a list of rows and a list of products generates a map that represents
 * relations between parent and container rows. The generated map will be in the following format:
 * - Key: stableRowID of a row which contains a product that is configured to have a container (deposit fee)
 * - Value: a list of stableRowIDs of container rows that belong to the given parent row
 *
 * @example
 * const rows = [
 *   { stableRowID: 123, productID: 321, amount: 1 },
 *   {
 *     stableRowID: 456,
 *     productID: 654,
 *     amount: 2,
 *     jdoc: { BrazilPOS: { parentRowID: 123 } },
 *   },
 * ];
 * const products = {
 *   321: { productID: 321, containerID: '654', containerAmount: 2 },
 * };
 * generateContainerMap(rows, products);
 * // Returns Map with entries: { 123: [456] }
 */
function generateContainerMap<
  Row extends MinimalRow,
  Product extends MinimalProduct
>(rows: Row[], products: Record<string, Product>) {
  const containerMap = new Map<number, number[]>();
  const usedContainers: number[] = [];

  rows.forEach(o => {
    const parent = { ...o, ...(products[o.productID] ?? {}) };

    if (!Number(parent.containerID)) return;

    const containers = findMatchingContainers(
      parent,
      rows.filter(row => !usedContainers.includes(Number(row.stableRowID))),
    );

    const containerIds = containers.map(container =>
      Number(container.stableRowID),
    );
    usedContainers.push(...containerIds);
    // All rows that have containerID are saved even if no containers were found
    containerMap.set(Number(parent.stableRowID), containerIds);
  });

  return containerMap;
}

/**
 * Returns a new list of rows that is modified as follows:
 * Goes over the list of rows, identifies which rows are containers (deposit fees)
 * and based on the return value of getShouldKeepContainers:
 * - if true then keeps the row unchanged
 * - if false then removes container rows that belong to the row and set
 * addContainerOverride field value based on whether the row had any containers
 *
 * The resulting list of rows will have addContainerOverride fields assigned and rows removed
 * to ensure that during cart calculation container rows will be added automatically if they
 * previously existed and will not be added if they did not previously exist or were added as regular rows.
 *
 * @example
 * const rows = [
 *   { stableRowID: 123, productID: 321, amount: 1 },
 *   {
 *     stableRowID: 456,
 *     productID: 654,
 *     amount: 2,
 *     jdoc: { BrazilPOS: { parentRowID: 123 } },
 *   },
 * ];
 * const products = {
 *   321: { productID: 321, containerID: 654, containerAmount: 2 },
 * };
 *
 * parseRowsWithContainers(rows, products, () => true);
 * // Returns: [
 * //   { stableRowID: 123, productID: 321, amount: 1 },
 * //   {
 * //     stableRowID: 456,
 * //     productID: 654,
 * //     amount: 2,
 * //     jdoc: { BrazilPOS: { parentRowID: 123 } },
 * //   },
 * // ]
 *
 * parseRowsWithContainers(rows, products, () => false);
 * // Returns: [ { stableRowID: 123, productID: 321, amount: 1, addContainerOverride: true } ]
 */
export function parseRowsWithContainers<
  Row extends MinimalRow,
  Product extends MinimalProduct
>(
  rows: Row[],
  products: Record<string, Product>,
  getShouldKeepContainers: (row: Row) => boolean,
) {
  const containerMap = generateContainerMap(rows, products);

  const toOmit: number[] = [];
  return rows
    .map(row => {
      if (getShouldKeepContainers(row)) return row;
      if (!containerMap.has(Number(row.stableRowID))) return row;

      const containerIds =
        containerMap.get(Number(row.stableRowID)) ?? ([] as number[]);

      toOmit.push(...containerIds);

      return {
        ...row,
        // This will ensure that during cart calculation containers will be added
        // based on whether or not the row originally had containers
        addContainerOverride: containerIds.length > 0,
      };
    })
    .filter(row => !toOmit.includes(Number(row.stableRowID)));
}
